import { el } from './dom';
import type { DispatchValue } from '../pipeline/dispatch';
import type { GeoHit } from '../lib/geocode';
import { RedactionStore } from '../lib/redactionStore';
import { PreviewCanvas } from './PreviewCanvas';
import { ReviewSidebar, type SectionKind } from './ReviewSidebar';
import type { ReviewSelection } from './types';

export type LivePreviewPanelProps = {
  value: DispatchValue;
  filename: string;
  outputFilename: string;
  notice?: string;
  allowedSections?: ReadonlySet<SectionKind>;
  geo?: GeoHit;
  onConfirm: (selection: ReviewSelection) => void;
  onCancel: () => void;
  /** Forwarded to the sidebar for batch mode's "save & next →" label. */
  confirmLabelHtml?: string;
  cancelLabel?: string;
  /**
   * Called when the user attempts a pro-only action while not having
   * pro (e.g. dragging a manual redaction or clicking the "redact this"
   * button on a text selection). Should show the upgrade modal and
   * return false to block the action; return true to allow it.
   */
  requirePro?: () => boolean;
};

export type LivePreviewPanel = {
  root: HTMLElement;
  store: RedactionStore;
  dispose(): void;
};

/**
 * Composition root for the in-app review surface. Owns the
 * RedactionStore. Mounts the live PreviewCanvas on the left and the
 * ReviewSidebar on the right. Both components subscribe to the same
 * store, so toggling an item in the sidebar instantly redraws the
 * preview.
 *
 * Output generation reuses the store: onConfirm fires with
 * store.snapshotSelection(), which redactDispatch consumes unchanged.
 */
export const LivePreviewPanel = (props: LivePreviewPanelProps): LivePreviewPanel => {
  const store = new RedactionStore(props.value);
  const root = el(`<section class="live-preview" id="review-panel" aria-live="polite"></section>`);
  const container = el(`<div class="container live-preview-grid"></div>`);
  root.append(container);

  const preview = PreviewCanvas(store, props.value, {
    ...(props.requirePro !== undefined && { requirePro: props.requirePro }),
  });
  const sidebar = ReviewSidebar({
    store,
    filename: props.filename,
    outputFilename: props.outputFilename,
    ...(props.notice !== undefined && { notice: props.notice }),
    ...(props.allowedSections !== undefined && { allowedSections: props.allowedSections }),
    ...(props.geo !== undefined && { geo: props.geo }),
    ...(props.confirmLabelHtml !== undefined && { confirmLabelHtml: props.confirmLabelHtml }),
    ...(props.cancelLabel !== undefined && { cancelLabel: props.cancelLabel }),
    onConfirm: props.onConfirm,
    onCancel: props.onCancel,
  });

  container.append(preview.root, sidebar.root);

  return {
    root,
    store,
    dispose(): void {
      preview.dispose();
      sidebar.dispose();
    },
  };
};
